import { useState } from 'react';
import {
  CheckCircle,
  DownloadFileIcon,
  XCircle
} from '../../../../icons/SvgIcons';
import { downloadBulkImportFailureReport } from '../../../../services/AISystemsService';
import { BulkImportResponse } from '../../../../types/AISystems.types';
import { PrimaryButton, WhiteButton } from '../../Button/Buttons';
import Modal from '../Modal';
import { Toast } from '../../Toast/Toast';

type ErrorLogModalProps = {
  importResult: BulkImportResponse | null;
  showErrorLogModal: boolean;
  handleDoneButton: () => void;
};

const ModalBulkImportErrorLog = ({
  importResult,
  showErrorLogModal,
  handleDoneButton
}: ErrorLogModalProps) => {
  const [downloading, setDownloading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const createdRows = importResult?.createdRows || 0;
  const updatedRows = importResult?.updatedRows || 0;
  const skippedRows = importResult?.skippedRows || 0;

  const handleErrorLogDownload = async () => {
    const failureReport = importResult?.failureReport;
    if (!failureReport?.reportId || downloading) return;

    setDownloading(true);
    try {
      const blob = await downloadBulkImportFailureReport(
        failureReport.reportId
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = failureReport.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowToast(true);
    } catch (error) {
      console.error('Failed to download bulk import failure report:', error);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <>
      <Modal open={showErrorLogModal} dialogPanelClassnames="w-[512px]">
        <div className="flex flex-col justify-between gap-4">
          <div className="flex flex-col gap-2">
            <span className="text-gray-900 text-lg font-medium">
              Import Completed with Exceptions
            </span>
            <p className="text-gray-500 text-sm">
              Your file has been processed, but some rows could not be imported.
              Please download the error log to review and fix the issues.
            </p>
          </div>
          <ul className="text-gray-900 text-sm">
            <li className="flex items-center gap-1">
              {CheckCircle()} {createdRows}{' '}
              {createdRows === 1 ? 'system' : 'systems'} created
            </li>
            <li className="flex items-center gap-1">
              {CheckCircle()}
              {updatedRows} {updatedRows === 1 ? 'system' : 'systems'} updated
            </li>
            <li className="flex items-center gap-1">
              {XCircle()}
              {skippedRows} {skippedRows === 1 ? 'row' : 'rows'} skipped
            </li>
          </ul>
          <div className="flex justify-end gap-3 h-[38px]">
            <WhiteButton
              icon={DownloadFileIcon()}
              onClick={handleErrorLogDownload}
              disabled={downloading || !importResult?.failureReport?.reportId}
            >
              {downloading ? 'Downloading...' : 'Download Error Log.xlsx'}
            </WhiteButton>
            <PrimaryButton onClick={handleDoneButton}>Done</PrimaryButton>
          </div>
        </div>
      </Modal>

      <Toast
        setShowToast={setShowToast}
        showToast={showToast}
        text="Error Log successfully downloaded!"
      />
    </>
  );
};

export default ModalBulkImportErrorLog;
